import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { documentApi } from "./api";
import type {
  AdminDocumentFilters,
  DocumentModuleCode,
  DocumentUploadRequest,
  MyDocumentFilters,
} from "./types";

const KEY = "documents";

/** Documents attached to a specific record (leave/claim/training/etc.) */
export function useDocumentsByRecord(
  moduleCode: DocumentModuleCode | string,
  referenceId: number | null | undefined,
) {
  return useQuery({
    queryKey: [KEY, "record", moduleCode, referenceId],
    queryFn: () => documentApi.listByRecord(moduleCode, referenceId!),
    enabled: !!referenceId,
    staleTime: 1000 * 30,
  });
}

/** Admin — all documents across the organisation */
export function useAllDocuments(
  page = 0,
  size = 20,
  filters: AdminDocumentFilters = {},
) {
  return useQuery({
    queryKey: [KEY, "all", page, size, filters],
    queryFn: () => documentApi.listAll({ page, size, ...filters }),
    staleTime: 1000 * 30,
    // Keep showing the previous page's results while a new filter/page is fetching,
    // instead of flipping back to `isLoading`/no-data — otherwise every filter change
    // (including a search keystroke) briefly unmounts the whole page via the caller's
    // `if (query.isLoading) return <PageLoader />`, which drops input focus.
    placeholderData: keepPreviousData,
  });
}

/** Employee self-service — documents uploaded by the current user */
export function useMyDocuments(
  page = 0,
  size = 20,
  filters: MyDocumentFilters = {},
) {
  return useQuery({
    queryKey: [KEY, "mine", page, size, filters],
    queryFn: () => documentApi.listMine({ page, size, ...filters }),
    staleTime: 1000 * 30,
    // Same reasoning as useAllDocuments above — keep prior results visible while
    // refetching so the search input never gets unmounted mid-type.
    placeholderData: keepPreviousData,
  });
}

export function useUploadDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, meta }: { file: File; meta: DocumentUploadRequest }) =>
      documentApi.upload(file, meta),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

// NOTE: no generic useDeleteDocument() — the backend removed the generic delete endpoint
// so deletion bypassing a module's own business rules (e.g. removing a closed claim's
// receipt) isn't possible anymore. Delete a document from the record it's attached to
// (e.g. claims/hooks.ts) instead.

/** Undo a soft delete — HR_DOCUMENT_MANAGE / SUPER_ADMIN only, no time limit. */
export function useRestoreDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => documentApi.restore(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

/** Fire when the in-app viewer opens a document — records a VIEW in the access log. */
export function useViewDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => documentApi.viewPing(id),
    // Best-effort — a failed ping shouldn't surface an error to the user, but do
    // refresh view counts / the access log if they happen to be open.
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

/** Who viewed/downloaded a document — HR_DOCUMENT_MANAGE / SUPER_ADMIN only. */
export function useDocumentAccessLog(
  documentId: number | null | undefined,
  page = 0,
  size = 10,
) {
  return useQuery({
    queryKey: [KEY, "access-log", documentId, page, size],
    queryFn: () => documentApi.getAccessLog(documentId!, page, size),
    enabled: !!documentId,
    staleTime: 1000 * 15,
  });
}
